import React, { useEffect, useState } from 'react';
import { useStateProgramContext } from './StateProgramContext';
import { useLocalization } from '../../store/utils/useLocalization';
import { Text, View } from 'react-native';
import StateProgramsError from './errors/StateProgramsError';

interface StateProgramSubmissionProps {
  userId: number;
  visitId: number;
  providerId: number;
  onSubmissionComplete?: () => void;
}

export function StateProgramSubmission(props: StateProgramSubmissionProps) {
  const { t } = useLocalization();
  const programServices = useStateProgramContext();
  const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);
  const [submissionError, setSubmissionError] = useState<Error | undefined>();

  const submitAttestation = async (): Promise<void> => {
    try {
      // Submit data to attestation service
      await programServices.attest(props.userId, props.visitId, props.providerId);
    } catch (cause) {
      // Mark the attestation as errored with any non-PII data
      setSubmissionError(
        new StateProgramsError(
          'Failed to submit attestation',
          cause,
          programServices.id,
          programServices.version,
          props.visitId,
          props.providerId
        )
      );
    } finally {
      // Mark the submission complete regardless of outcome
      setIsSubmissionComplete(true);
      // Notify parent component that submission is complete
      props.onSubmissionComplete?.();
    }
  };

  useEffect(() => {
    // fire and forget the async attestation, let it update when ready
    submitAttestation();
  }, [
    // properties that should trigger resubmission
    props.userId,
    props.visitId,
    // parts of program service that should trigger resubmission
    programServices.id,
    programServices.version,
    programServices.completeAttestationData,
  ]);

  if (submissionError !== undefined) {
    // propagate the error and let the wizard error boundary catch it
    throw submissionError;
  }

  return (
    <View>
      <Text>
        {isSubmissionComplete
          ? t('state_program_attestation_submitted', { programName: programServices.name })
          : t('app_dialog_loading')}
      </Text>
    </View>
  );
}
